'use client';

import {
  useState,
  useCallback,
  useMemo,
  useDeferredValue,
  useRef,
  useEffect,
  memo,
  type ReactNode,
} from 'react';
import { ArrowLeft, ChevronRight, Search, X } from 'lucide-react';
import { Button } from '@/hen-ui/atomics/Button';
import { Link } from '@/hen-ui/atomics/Link';
import { List, ListItem } from '@/hen-ui/atomics/List';
import { Input } from '@/hen-ui/atomics/Input';
import { Text } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';
import { Section } from '@/hen-ui/composites/Section';
import type {
  NavItem,
  SubNavCta,
  SubNavMobileBackColor,
  SubNavMobileHeader,
  SubNavMobileHeaderDivider,
  SubNavMobileItemSize,
  SubNavMobileLinkWeight,
  SubNavPanelStyle,
} from './types';
import {
  filterSections,
  hasNavSubmenu,
  hasSubNavCtaMessage,
  hasValidHref,
  isColumnsLayout,
  isFeaturedColumn,
} from './utils';
import { MobileFeaturedCard } from './MobileFeaturedCard';
import { SubNavFooter } from './SubNavFooter';
import { MenuContentSlot, useMenuContentSlot } from './MenuContentSlot';
import { FOCUS_RING } from './subnav.constants';
import { SUBNAV_LINK_WEIGHT_CLASS } from './subnav.variants';
import { cn } from '@/hen-ui/utils';
import { SPACE_GAP } from '@/hen-ui/utils/space-steps';

/** Drops Link's `hover:decoration-link-underline-hover` via twMerge so Navbar drawer links keep a text-colored underline. */
const NAV_LINK_DECORATION_OPT_OUT = 'hover:decoration-current';

type MobileTextStyle = Pick<
  SubNavPanelStyle,
  'labelStyle' | 'linkStyle' | 'linkGap'
>;

const NO_TEXT_STYLE: MobileTextStyle = {};

interface ItemSizeClasses {
  /** Rows of a tier 1 or tier 2 list. */
  list: string;
  /** Side padding of a row; its vertical padding is on the row's control. */
  row: string;
  /** Text of a row that opens the next tier. */
  opener: string;
  leafLink: string;
  leafText: { variant: 'b3' | 'b5'; color?: 'heading' };
  chevronSize: number;
  chevron: string;
  /** Tier 3 column list. */
  columns: string;
  column: string;
  columnLabelSpace: string;
  /** Each tier 3 link; `''` keeps the link inline. */
  link: string;
  /** Side padding of the featured and menu content blocks. */
  extra: string;
}

const ITEM_SIZES: Record<SubNavMobileItemSize, ItemSizeClasses> = {
  compact: {
    list: 'gap-0',
    row: 'px-sm gap-0',
    opener: 'text-b5 text-heading hover:text-heading',
    leafLink: '',
    leafText: { variant: 'b5', color: 'heading' },
    chevronSize: 16,
    chevron: 'text-neutral-400',
    columns: 'gap-0',
    column: 'px-sm py-xs gap-0',
    columnLabelSpace: 'mb-xxs',
    link: 'py-xxs',
    extra: 'px-sm',
  },
  touch: {
    list: 'gap-0 py-lg',
    row: 'px-md gap-0',
    opener: 'text-b3 text-body hover:text-body',
    leafLink: 'text-b3 text-body',
    leafText: { variant: 'b3' },
    chevronSize: 24,
    chevron: 'text-body',
    columns: 'gap-0 pb-lg',
    column: 'px-md pt-lg gap-0',
    columnLabelSpace: 'mb-xs',
    link: 'flex py-sm',
    extra: 'px-md',
  },
};

// The vertical padding is on the row's button or link, so the whole row is
// the tap target. The flush Button size forces `!p-0`.
const ROW_PAD = 'py-sm';
const OPENER_ROW_PAD = '!py-sm';

const ROW_DIVIDERS = 'divide-y divide-neutral-100';
const EXTRA_DIVIDER = 'border-t border-neutral-100';

const HEADER_DIVIDERS: Record<SubNavMobileHeaderDivider, string> = {
  subtle: 'border-b border-neutral-100',
  divider: 'border-b border-border-divider',
  strong: 'border-b border-border-divider-strong',
  accent: 'border-b border-nav-hover-accent',
  none: '',
};

const BACK_COLORS: Record<SubNavMobileBackColor, string> = {
  accent: 'text-primary-700',
  neutral: 'text-heading',
};

// textOnly defaults to hover:text-subtext, which sticks on touch.
const BACK_LINK_COLORS: Record<SubNavMobileBackColor, string> = {
  accent: 'text-primary-700 hover:text-primary-700 active:text-primary-700',
  neutral: 'text-heading hover:text-heading active:text-heading',
};

interface MobileRowStyle {
  itemSize: SubNavMobileItemSize;
  dividers: boolean;
  linkWeight: SubNavMobileLinkWeight;
}

interface MobileItemListProps extends MobileRowStyle {
  items: NavItem[];
  onSelectItem: (item: NavItem, opener: HTMLButtonElement) => void;
}

const MobileItemList = memo(function MobileItemList({
  items,
  onSelectItem,
  itemSize,
  dividers,
  linkWeight,
}: MobileItemListProps) {
  const styles = ITEM_SIZES[itemSize];
  const weight = SUBNAV_LINK_WEIGHT_CLASS[linkWeight];
  return (
    <Section
      as='div'
      padding='none'
      gap='min'
      className={cn(styles.list, dividers && ROW_DIVIDERS)}
    >
      {items.map((item) => {
        const hasChildren = hasNavSubmenu(item);

        return hasChildren ? (
          <Section
            key={item.id}
            as='div'
            padding='none'
            gap='min'
            className={styles.row}
          >
            <Button
              type='button'
              variant='textOnlyNeutral'
              size='flush'
              data-mobile-nav-opener={item.id}
              className={cn(
                'flex w-full items-center justify-between text-left no-underline hover:no-underline',
                OPENER_ROW_PAD,
                styles.opener,
                weight,
                FOCUS_RING,
              )}
              onClick={(event) => onSelectItem(item, event.currentTarget)}
            >
              {item.label}
              <ChevronRight
                size={styles.chevronSize}
                className={styles.chevron}
                aria-hidden='true'
              />
            </Button>
          </Section>
        ) : (
          <Section
            key={item.id}
            as='div'
            padding='none'
            gap='min'
            className={styles.row}
          >
            {hasValidHref(item) ? (
              <Link
                href={item.uri}
                variant='black'
                size='sm'
                underline='hover'
                underlineStyle='thin'
                className={cn(
                  'flex w-full',
                  ROW_PAD,
                  styles.leafLink,
                  weight,
                  FOCUS_RING,
                  NAV_LINK_DECORATION_OPT_OUT,
                )}
              >
                {item.label}
              </Link>
            ) : (
              <Text
                tag='span'
                variant={styles.leafText.variant}
                weight={linkWeight}
                color={styles.leafText.color}
                className={cn('block', ROW_PAD)}
              >
                {item.label}
              </Text>
            )}
          </Section>
        );
      })}
    </Section>
  );
});

// ─── Tier 3: column links + featured cards ────────────────────────────────────

interface MobileColumnLabelProps {
  column: NavItem;
  labelStyle: NonNullable<SubNavPanelStyle['labelStyle']>;
  className: string;
}

function MobileColumnLabel({
  column,
  labelStyle,
  className,
}: MobileColumnLabelProps) {
  const isSubtext = labelStyle === 'subtext';
  if (hasValidHref(column)) {
    return (
      <Link
        href={column.uri}
        variant={isSubtext ? 'subtext' : 'black'}
        size='sm'
        underline='hover'
        underlineStyle='thin'
        className={cn(
          className,
          isSubtext ? 'text-b5 font-normal' : 'font-bold',
          FOCUS_RING,
          NAV_LINK_DECORATION_OPT_OUT,
        )}
      >
        {column.label}
      </Link>
    );
  }
  return (
    <Text
      variant='b5'
      weight={isSubtext ? 'regular' : 'bold'}
      color={isSubtext ? 'subtext' : 'heading'}
      className={className}
    >
      {column.label}
    </Text>
  );
}

interface MobileTier3Props {
  subsection: NavItem;
  hideFeatured?: boolean;
  itemSize: SubNavMobileItemSize;
  dividers: boolean;
  textStyle: MobileTextStyle;
}

const MobileTier3 = memo(function MobileTier3({
  subsection,
  hideFeatured = false,
  itemSize,
  dividers,
  textStyle,
}: MobileTier3Props) {
  const styles = ITEM_SIZES[itemSize];
  const isTouch = itemSize === 'touch';
  const { labelStyle = 'heading', linkStyle = 'subtext', linkGap } = textStyle;
  const linkListGap = isTouch
    ? 'gap-0'
    : linkGap
      ? SPACE_GAP[linkGap]
      : 'gap-xxs';
  const linkText = cn(
    (isTouch || linkStyle === 'body') && 'text-b3',
    linkStyle === 'body' ? 'text-body' : 'text-neutral-500',
  );
  const columns = (subsection.children ?? []).filter(
    (col) => !isFeaturedColumn(col),
  );
  const featuredColumn =
    (subsection.children ?? []).find(isFeaturedColumn) ?? null;
  const featuredItems = (featuredColumn?.children ?? []).filter(
    (item) => item.type === 'featured',
  );

  return (
    <Flex direction='column' gap='none'>
      <Section
        as='div'
        padding='none'
        gap='min'
        className={cn(styles.columns, dividers && ROW_DIVIDERS)}
      >
        {columns.map((column) => {
          const links = (column.children ?? []).filter(
            (child) => child.type !== 'action',
          );
          const actions = (column.children ?? []).filter(
            (child) => child.type === 'action' && hasValidHref(child),
          );

          return (
            <Section
              key={column.id}
              as='div'
              padding='none'
              gap='min'
              className={styles.column}
            >
              <MobileColumnLabel
                column={column}
                labelStyle={labelStyle}
                className={styles.columnLabelSpace}
              />

              {links.length > 0 && (
                <List bulletStyle='none' className={linkListGap}>
                  {links.map((link) => {
                    const weight =
                      link.type === 'highlighted' ? 'font-bold' : 'font-normal';
                    return (
                      <ListItem key={link.id}>
                        {hasValidHref(link) ? (
                          <Link
                            href={link.uri}
                            variant='subtext'
                            size='md'
                            underline='hover'
                            underlineStyle='thin'
                            className={cn(
                              styles.link,
                              linkText,
                              weight,
                              FOCUS_RING,
                              NAV_LINK_DECORATION_OPT_OUT,
                            )}
                          >
                            {link.label}
                          </Link>
                        ) : (
                          <span
                            className={cn(
                              'text-base',
                              styles.link,
                              linkText,
                              weight,
                            )}
                          >
                            {link.label}
                          </span>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              )}

              {actions.map((action) => (
                <Link
                  key={action.id}
                  href={action.uri}
                  variant='primary'
                  size='sm'
                  underline='none'
                  underlineStyle='thin'
                  className={cn(
                    'mt-xs inline-flex font-bold',
                    FOCUS_RING,
                    NAV_LINK_DECORATION_OPT_OUT,
                  )}
                >
                  {action.label}
                </Link>
              ))}
            </Section>
          );
        })}
      </Section>

      {!hideFeatured && featuredItems.length > 0 && (
        <Section
          as='div'
          padding='none'
          gap='min'
          className={cn(
            styles.extra,
            dividers && EXTRA_DIVIDER,
            'pb-md pt-sm gap-0',
          )}
        >
          {featuredColumn?.label && (
            <Text
              variant='l1'
              weight='bold'
              color='subtext'
              className='uppercase tracking-wide'
            >
              {featuredColumn.label}
            </Text>
          )}
          <Flex direction='column' gap='sm'>
            {featuredItems.map((item) => (
              <MobileFeaturedCard key={item.id} item={item} />
            ))}
          </Flex>
        </Section>
      )}
    </Flex>
  );
});

// ─── Main MobileLayout ────────────────────────────────────────────────────────

export interface MobileLayoutProps {
  /** Full top-level nav items — drives Tier 1. */
  items: NavItem[];
  cta?: SubNavCta;
  /** Called when the ✕ close button is tapped. */
  onClose: () => void;
  /**
   * Authored content matched to the selected top-level item's label. Shows
   * on that item's own tier: below its sections, or with its columns.
   */
  menuContent?: ReactNode;
  /**
   * `'compact'` is a row of controls with a back link named for the previous
   * tier, and each tier's title below it. `'title'` names the current tier
   * in the header: `title` on the first tier, a back arrow and the tier's
   * title on the others. The header stays pinned while the tier scrolls.
   * @default 'compact'
   */
  header?: SubNavMobileHeader;
  /** First-tier title with `header="title"`. */
  title?: string;
  /** Shows the button that filters the sections. @default true */
  showSearch?: boolean;
  /**
   * Shows the ✕ button in the header. Turn off when the host renders its own
   * close button. @default true
   */
  showClose?: boolean;
  /**
   * `'compact'` rows are small text in the heading color. `'touch'` rows are
   * touch-size rows in body text, and every column link is a full row.
   * @default 'compact'
   */
  itemSize?: SubNavMobileItemSize;
  /**
   * Lines between the rows and the column groups, and above the featured
   * and menu content blocks.
   * @default true
   */
  dividers?: boolean;
  /** Weight of the tier 1 and tier 2 rows. @default 'bold' */
  linkWeight?: SubNavMobileLinkWeight;
  /** Line under the header and the filter row. @default 'subtle' */
  headerDivider?: SubNavMobileHeaderDivider;
  /**
   * Back button color. Unset follows `header`: `'accent'` with the compact
   * header, `'neutral'` with the title header.
   */
  backColor?: SubNavMobileBackColor;
  /**
   * Column heading and link looks, as in the desktop panel. Body links are
   * b3. `linkGap` spaces `'compact'` links; `'touch'` links are rows instead.
   */
  textStyle?: MobileTextStyle;
}

export function MobileLayout({
  items,
  cta,
  onClose,
  menuContent,
  header = 'compact',
  title,
  showSearch = true,
  showClose = true,
  itemSize = 'compact',
  dividers = true,
  linkWeight = 'bold',
  headerDivider = 'subtle',
  backColor,
  textStyle = NO_TEXT_STYLE,
}: MobileLayoutProps) {
  const styles = ITEM_SIZES[itemSize];
  const isTitleHeader = header === 'title';
  const backTone = backColor || (isTitleHeader ? 'neutral' : 'accent');
  const headerDividerClass = HEADER_DIVIDERS[headerDivider];
  const rowStyle: MobileRowStyle = { itemSize, dividers, linkWeight };
  // Navigation drill-down state (tier, selectedItem, selectedSubsection) is kept
  // internal intentionally. MobileLayout is always rendered inside the Navbar
  // Drawer, which owns the open/close lifecycle. The drill-down position is
  // ephemeral UI state that resets naturally when the Drawer closes — there is
  // no consumer use-case for persisting or controlling it from the outside.
  const [tier, setTier] = useState(1);
  const [selectedItem, setSelectedItem] = useState<NavItem | null>(null);
  const [selectedSubsection, setSelectedSubsection] = useState<NavItem | null>(
    null,
  );

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const openerStack = useRef<HTMLButtonElement[]>([]);
  const pendingFocusId = useRef<string | null>(null);

  const deferredQuery = useDeferredValue(searchQuery);
  const trimmedQuery = deferredQuery.trim();

  const filteredItems = useMemo(
    () => (trimmedQuery ? filterSections(items, trimmedQuery) : items),
    [items, trimmedQuery],
  );

  const displayTier = trimmedQuery ? 1 : tier;

  // A `columns` item has no sections tier: its children are already the
  // columns Tier 3 renders, so it drills straight there and Back returns to
  // Tier 1.
  const goToItem = useCallback((item: NavItem, opener: HTMLButtonElement) => {
    openerStack.current.push(opener);
    setSelectedItem(item);
    if (isColumnsLayout(item)) {
      setSelectedSubsection(item);
      setTier(3);
      return;
    }
    setTier(2);
  }, []);

  const goToSubsection = useCallback(
    (subsection: NavItem, opener: HTMLButtonElement) => {
      openerStack.current.push(opener);
      setSelectedSubsection(subsection);
      setTier(3);
    },
    [],
  );

  const selectedIsColumns = isColumnsLayout(selectedItem);
  const menuContentSlot = useMenuContentSlot(selectedItem?.label ?? '');

  const goBack = useCallback(() => {
    const opener = openerStack.current.pop();
    pendingFocusId.current = opener?.dataset.mobileNavOpener ?? null;
    // Keep focus inside the drawer before the focused row unmounts. Otherwise
    // the dialog treats the unmount as focus leaving and closes.
    rootRef.current?.focus();
    setTier((t) => (t === 3 && selectedIsColumns ? 1 : Math.max(1, t - 1)));
  }, [selectedIsColumns]);

  const toggleSearch = useCallback(() => {
    setIsSearchOpen((prev) => {
      if (prev) setSearchQuery('');
      return !prev;
    });
  }, []);

  useEffect(() => {
    if (isSearchOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const id = pendingFocusId.current;
    if (!id || !rootRef.current) return;
    const opener = rootRef.current.querySelector<HTMLButtonElement>(
      `[data-mobile-nav-opener="${CSS.escape(id)}"]`,
    );
    if (!opener) return;
    opener.focus();
    pendingFocusId.current = null;
  }, [tier, displayTier]);

  useEffect(() => {
    if (displayTier <= 1) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      // The dialog dismisses from its own document listener even after
      // preventDefault. Stopping the event here keeps that listener from running.
      event.stopPropagation();
      goBack();
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [displayTier, goBack]);

  const showMenuContent =
    menuContent != null &&
    selectedItem !== null &&
    (displayTier === 2 || (displayTier === 3 && selectedIsColumns));

  const menuContentSection = showMenuContent ? (
    <MenuContentSlot
      value={menuContentSlot.value}
      className={cn(
        'flex flex-col gap-sm pb-md pt-sm',
        styles.extra,
        dividers && EXTRA_DIVIDER,
      )}
    >
      {menuContent}
    </MenuContentSlot>
  ) : null;

  // Back label: what you tapped to arrive at the current tier
  const backLabel =
    displayTier === 3 && !selectedIsColumns
      ? (selectedItem?.label ?? '')
      : isTitleHeader && title
        ? title
        : 'All';
  const tierTitle =
    displayTier === 3
      ? selectedSubsection?.label
      : displayTier === 2
        ? selectedItem?.label
        : title;
  const headerIconClass = cn(
    'p-xxs text-neutral-700',
    isTitleHeader ? '[&_svg]:!size-6' : '[&_svg]:!size-5',
    FOCUS_RING,
  );
  const headerIconSize = isTitleHeader ? 24 : 20;

  return (
    <Flex
      ref={rootRef}
      direction='column'
      gap='none'
      tabIndex={-1}
      className='outline-none'
    >
      <div className='sticky top-0 z-10 bg-white'>
        {/*
         * ── Unified header ────────────────────────────────────────────────────
         * compact, tier 1: only search + X
         * compact, tier 2: ← All + search + X
         * compact, tier 3: ← {selectedItem.label} + search + X
         * title: [← ] {tier title} + search + X
         */}
        <Flex
          direction='row'
          align='center'
          gap={isTitleHeader ? 'sm' : 'none'}
          className={cn(
            headerDividerClass,
            isTitleHeader ? 'px-lg py-lg' : 'px-sm py-xs',
          )}
        >
          {displayTier > 1 &&
            (isTitleHeader ? (
              <Button
                type='button'
                variant='iconOnly'
                size='flush'
                aria-label={`Back to ${backLabel}`}
                className={cn(headerIconClass, BACK_COLORS[backTone])}
                onClick={goBack}
              >
                <ArrowLeft
                  size={headerIconSize}
                  className='flex-shrink-0'
                  aria-hidden='true'
                />
              </Button>
            ) : (
              <Button
                type='button'
                variant='textOnly'
                size='flush'
                aria-label={`Back to ${backLabel}`}
                className={cn(
                  'gap-xxs text-b5 font-bold no-underline hover:no-underline',
                  BACK_LINK_COLORS[backTone],
                  FOCUS_RING,
                )}
                onClick={goBack}
              >
                <ArrowLeft size={16} aria-hidden='true' />
                {backLabel}
              </Button>
            ))}

          {isTitleHeader && tierTitle && (
            <span
              data-mobile-nav-title
              className='min-w-0 truncate font-heading text-h4 font-bold text-heading'
            >
              {tierTitle}
            </span>
          )}

          <Flex direction='row' align='center' gap='xxs' className='ml-auto'>
            {showSearch && (
              <Button
                type='button'
                variant='iconOnly'
                size='flush'
                aria-label={isSearchOpen ? 'Close search' : 'Search'}
                className={headerIconClass}
                onClick={toggleSearch}
              >
                <Search
                  size={headerIconSize}
                  className='flex-shrink-0'
                  aria-hidden='true'
                />
              </Button>
            )}
            {showClose && (
              <Button
                type='button'
                variant='iconOnly'
                size='flush'
                aria-label='Close navigation'
                className={headerIconClass}
                onClick={onClose}
              >
                <X
                  size={headerIconSize}
                  className='flex-shrink-0'
                  aria-hidden='true'
                />
              </Button>
            )}
          </Flex>
        </Flex>

        {/* Search input — revealed below header */}
        {isSearchOpen && (
          <Section
            as='div'
            padding='none'
            gap='min'
            className={cn(headerDividerClass, 'px-sm py-xs gap-0')}
          >
            <Input
              ref={searchInputRef}
              type='text'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Filter sections...'
              aria-label='Filter menu sections'
            />
          </Section>
        )}
      </div>

      {/* ── Current tier content ─────────────────────────────────────────────── */}

      {displayTier === 1 &&
        (trimmedQuery && filteredItems.length === 0 ? (
          <Section
            as='div'
            padding='none'
            gap='min'
            className='px-sm py-xl text-center text-b5 text-neutral-500 gap-0'
          >
            No results for &ldquo;{searchQuery}&rdquo;
          </Section>
        ) : (
          <MobileItemList
            items={filteredItems}
            onSelectItem={goToItem}
            {...rowStyle}
          />
        ))}

      {displayTier === 2 && selectedItem ? (
        <>
          {!isTitleHeader && (
            <Section
              as='div'
              padding='none'
              gap='min'
              className='px-sm py-lg gap-0'
            >
              <Text tag='span' variant='b2' weight='bold' color='heading'>
                {selectedItem.label}
              </Text>
            </Section>
          )}
          <MobileItemList
            items={selectedItem.children ?? []}
            onSelectItem={goToSubsection}
            {...rowStyle}
          />
          {menuContentSection}
        </>
      ) : null}

      {displayTier === 3 && selectedSubsection && (
        <>
          {!isTitleHeader && (
            <Section
              as='div'
              padding='none'
              gap='min'
              className='px-sm py-lg gap-0'
            >
              <Text tag='span' variant='b2' weight='bold' color='heading'>
                {selectedSubsection.label}
              </Text>
            </Section>
          )}
          <MobileTier3
            subsection={selectedSubsection}
            hideFeatured={selectedIsColumns && menuContentSlot.hasContent}
            itemSize={itemSize}
            dividers={dividers}
            textStyle={textStyle}
          />
          {menuContentSection}
        </>
      )}

      {/* CTA — only on Tier 1 */}
      {hasSubNavCtaMessage(cta) && displayTier === 1 && (
        <Section
          as='div'
          padding='none'
          gap='min'
          className='px-sm pb-lg pt-sm gap-0'
        >
          <SubNavFooter cta={cta} variant='mobile' />
        </Section>
      )}
    </Flex>
  );
}
